import { useQuery } from "@apollo/client";
import { uniq } from "lodash";
import moment from "moment";
import {
  GetProfileOneononeQueryQuery,
  GetProfileOneononeQueryQueryVariables,
} from "types/graphql-schema";

import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Loading from "@components/loading/loading";
import { assertEdgesNonNull } from "@helpers/helpers";

import getProfileOneononeQuery from "../graphql/get-profile-oneonone-query";
import ProfileLastOneononeCard from "./profile-last-oneonone-card";
import ProfileNextOneononeCard from "./profile-next-oneonone-card";
import ProfileNoOneononeSetup from "./profile-no-oneonone-setup";

const ProfileOneononeCards = ({
  profileId,
  profileName,
  profileFirstName,
  profileEmail,
}: {
  profileId: number;
  profileName: string;
  profileFirstName: string;
  profileEmail: string;
}) => {
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const startOfHour = moment().startOf("hour").format();

  const { data, loading } = useQuery<
    GetProfileOneononeQueryQuery,
    GetProfileOneononeQueryQueryVariables
  >(getProfileOneononeQuery, {
    variables: {
      participants: uniq([currentUser.id, profileId]),
      nextAfterDate: startOfHour,
      lastBeforeDate: startOfHour,
    },
    skip: !profileId || !organization.featureFlags.meetings,
  });

  if (!organization.featureFlags.meetings) {
    return null;
  }

  if (loading) {
    return <Loading>Loading 1:1 data...</Loading>;
  }

  const nextOneonone = data?.nextOneonone?.edges?.[0]?.node;
  const lastOneonone = data?.lastOneonone?.edges?.[0]?.node;

  const candidateMeetings = data?.candidateMeetings
    ? assertEdgesNonNull(data.candidateMeetings)
    : [];

  // Deduplicate by meetingGroupId to get unique recurring series
  const uniqueCandidates = candidateMeetings.reduce(
    (acc, meeting) => {
      if (!acc.seen.has(meeting.meetingGroupId)) {
        acc.seen.add(meeting.meetingGroupId);
        acc.meetings.push(meeting);
      }
      return acc;
    },
    { seen: new Set<number>(), meetings: [] as typeof candidateMeetings },
  ).meetings;

  return (
    <div className="flex flex-col gap-6">
      {nextOneonone ? (
        <ProfileNextOneononeCard meeting={nextOneonone} profileId={profileId} />
      ) : currentUser.id !== profileId ? (
        <ProfileNoOneononeSetup
          profileId={profileId}
          profileName={profileName}
          profileFirstName={profileFirstName}
          profileEmail={profileEmail}
          candidateMeetings={uniqueCandidates}
        />
      ) : null}
      {lastOneonone && <ProfileLastOneononeCard meeting={lastOneonone} />}
    </div>
  );
};

export default ProfileOneononeCards;
